import { GripVertical, ImagePlus, LoaderCircle, Star, UploadCloud } from "lucide-react";
import { useState } from "react";

const assetImageSrc = (asset) => asset.thumb_url || asset.optimized_url || asset.secure_url;

export const ListingMediaManager = ({
  editingListingId,
  fileInputRef,
  listingForm,
  onDropFiles,
  onFileSelection,
  onRemove,
  onReorder,
  onSetPrimary,
  uploading,
}) => {
  const [draggedPublicId, setDraggedPublicId] = useState("");
  const gallery = listingForm.admin_media?.gallery || [];
  const primaryPublicId = listingForm.admin_media?.primary_image?.public_id;

  const handleDrop = async (targetPublicId) => {
    if (!draggedPublicId || draggedPublicId === targetPublicId) {
      setDraggedPublicId("");
      return;
    }
    await onReorder(draggedPublicId, targetPublicId);
    setDraggedPublicId("");
  };

  return (
    <div className="rounded-[28px] border border-[#D4AF37]/30 bg-[#fffaf1] p-5" data-testid="listing-form-media-manager-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Media Management</p>
          <h3 className="mt-2 text-2xl font-semibold text-[#001935]">Admin-uploaded premium photo overrides</h3>
          <p className="mt-3 text-sm leading-7 text-[#42566f]">
            Upload high-resolution photos directly from your computer. Drag cards to reorder the gallery — the first image becomes the hero override.
          </p>
        </div>
        <button className="outline-button" data-testid="listing-media-open-file-picker-button" onClick={() => fileInputRef.current?.click()} type="button">
          <ImagePlus className="mr-2 h-4 w-4" /> Select Files
        </button>
      </div>

      <input accept="image/*" className="hidden" data-testid="listing-media-file-input" multiple onChange={onFileSelection} ref={fileInputRef} type="file" />
      <button
        className="mt-5 flex w-full flex-col items-center justify-center rounded-[24px] border border-dashed border-[#D4AF37]/40 bg-white px-6 py-10 text-center text-sm text-[#42566f] transition hover:border-[#D4AF37]/80"
        data-testid="listing-media-dropzone"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          onDropFiles(Array.from(event.dataTransfer.files || []));
        }}
        type="button"
      >
        <UploadCloud className="h-8 w-8 text-[#8b6d16]" />
        <span className="mt-4 font-semibold text-[#001935]">Drag and drop listing photos here</span>
        <span className="mt-2 max-w-xl">{editingListingId ? "Cloudinary will optimize and CDN-serve these images automatically." : "Save the listing first to activate Cloudinary uploads for this record."}</span>
      </button>

      {uploading ? (
        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-white px-4 py-2 text-sm text-[#8b6d16]" data-testid="listing-media-uploading-indicator">
          <LoaderCircle className="h-4 w-4 animate-spin" /> Uploading premium media...
        </div>
      ) : null}

      {gallery.length ? (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {gallery.map((asset, index) => {
            const isPrimary = primaryPublicId === asset.public_id;
            return (
              <div
                className={`overflow-hidden rounded-[24px] border bg-white transition ${draggedPublicId === asset.public_id ? "border-[#D4AF37] shadow-[0_18px_44px_rgba(212,175,55,0.18)]" : "border-[#D4AF37]/25"}`}
                data-testid={`admin-media-asset-card-${index + 1}`}
                draggable
                key={asset.public_id}
                onDragEnd={() => setDraggedPublicId("")}
                onDragOver={(event) => event.preventDefault()}
                onDragStart={() => setDraggedPublicId(asset.public_id)}
                onDrop={() => handleDrop(asset.public_id)}
              >
                <div className="relative">
                  <img alt={`Uploaded asset ${index + 1}`} className="aspect-[1.1/1] w-full object-cover" src={assetImageSrc(asset)} />
                  <div className="absolute left-3 top-3 flex flex-wrap gap-2">
                    {isPrimary ? <span className="rounded-full bg-[#001935] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#F5D98C]">Hero Override</span> : null}
                    <span className="rounded-full bg-white/92 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#42566f]">Slot {index + 1}</span>
                  </div>
                </div>
                <div className="space-y-3 px-4 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="truncate text-sm text-[#42566f]">{asset.public_id}</p>
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#001935]/10 bg-[#f8f4ea] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]" data-testid={`admin-media-drag-handle-${index + 1}`}>
                      <GripVertical className="h-4 w-4" /> Drag
                    </div>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <button className="outline-button !justify-center !px-4 !py-2 text-xs" data-testid={`admin-media-set-primary-button-${index + 1}`} onClick={() => onSetPrimary(asset.public_id)} type="button">
                      <Star className="mr-2 h-4 w-4" /> {isPrimary ? "Primary" : "Set Hero"}
                    </button>
                    <button className="rounded-full border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-red-600 transition hover:bg-red-100" data-testid={`admin-media-remove-button-${index + 1}`} onClick={() => onRemove(asset.public_id)} type="button">
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="mt-5 text-sm text-[#6b6252]" data-testid="listing-media-empty-state">No admin-uploaded override images yet.</p>
      )}
    </div>
  );
};